import OptionBuyingStrategySelect from '@/components/UI/OptionBuyingStrategySelect';
import { useTradingStrategies } from '@/hooks/useTradingStrategies';
import { optionLegFields, type OptimizerOptionOverrides, type OptionLegField } from './jobConfigInput';
import type { OptimizerPreflight } from './types';

export default function OptionLegConfigFields({
    strategyId,
    overrides,
    onChange,
    preflight
}: {
    strategyId: number | null;
    overrides: OptimizerOptionOverrides;
    onChange: (key: OptionLegField, value: number | null) => void;
    preflight: OptimizerPreflight | null;
}) {
    const { strategies, isLoading, error } = useTradingStrategies();
    const strategy = strategies.find((item) => item.id === strategyId);
    const resolved =
        preflight?.outcomeModeResolution.resolvedStrategyId === strategyId
            ? preflight.outcomeModeResolution.resolvedOptionLegStrategyIds
            : null;

    if (strategyId === null) return null;
    if (error) return <p className="text-red-200 text-sm mt-3">Could not load option legs: {error.message}</p>;
    if (isLoading) return <p className="text-zinc-400 text-sm mt-3">Loading option legs…</p>;
    if (!strategy) return <p className="text-yellow-200 text-sm mt-3">Strategy details unavailable; option selections cannot be edited.</p>;

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {optionLegFields.map((field) => {
                const leg = strategy.legs.find((item) => item.number === field.number && item.derivativeType === 'Option');
                if (!leg) return null;
                const resolvedId = resolved?.[field.resolvedKey];
                return (
                    <OptionBuyingStrategySelect
                        key={field.key}
                        id={`opt-${field.key}`}
                        label={`Leg ${field.number}: ${leg.name} — option buying strategy`}
                        mode="single"
                        placeholder="Use strategy default"
                        value={overrides[field.key] ?? null}
                        helpText={`Blank uses the strategy default. ${resolvedId ? `Checked selection: #${resolvedId}.` : 'Check the dataset to resolve the selection.'}`}
                        onChange={(value) => onChange(field.key, value)}
                    />
                );
            })}
        </div>
    );
}
